import { getOrderContentColumnScrollEl, isOrderContentColumnAtScrollRest } from "./orderMealsScrollChaining";
import type { OrderMealsLayoutSyncOptions } from "./orderStickyChrome";
import { resetMealsListViewportTopAtColumnScrollRest, syncOrderMealsListViewportTopCssVar, syncOrderStickyChromeCssVars } from "./orderStickyChrome";

/** Sync CSS variables for wide meal-list cap (no inline max-height — avoids measure/resize loops). */
export function syncOrderMealsScrollLayoutCssVars(options: OrderMealsLayoutSyncOptions = {}): void {
    syncOrderStickyChromeCssVars(options);
    if (options.force || isOrderContentColumnAtScrollRest()) {
        syncOrderMealsListViewportTopCssVar(options);
    }
}

const ORDER_MEALS_LAYOUT_CSS_VARS = [
    "--order-meals-list-viewport-top",
    "--order-info-banner-height",
    "--order-day-chrome-sticky-height",
    "--order-day-selector-sticky-height",
    "--order-catalog-sticky-height",
    "--order-amount-block-height",
    "--order-sticky-actions-height",
    "--order-content-column-visible-height",
] as const;

export function clearOrderMealsScrollportMaxHeight(): void {
    const root = document.documentElement.style;
    ORDER_MEALS_LAYOUT_CSS_VARS.forEach((name) => root.removeProperty(name));
    resetMealsListViewportTopAtColumnScrollRest();
}

/** Element that scrolls the meal list on wide layout (`.order-body-scroll` when sticky actions). */
export function getOrderMealsListScrollEl(): HTMLElement | null {
    const bodyScroll = document.querySelector<HTMLElement>(
        "#orderApp .order-scroll-sticky-region--has-actions:not(.order-scroll-sticky-region--no-meals) .order-body-scroll:not(.order-body-scroll--compact)",
    );
    if (bodyScroll) {
        return bodyScroll;
    }
    return document.querySelector<HTMLElement>(
        "#orderApp .order-meals-scrollport:not(.order-meals-scrollport--compact)",
    );
}

export function isWideMealsListBodyScroll(el: HTMLElement): boolean {
    return el.classList.contains("order-body-scroll");
}

/**
 * Keep meal-list viewport-top in sync while the column scrolls (wheel chain at list bottom).
 * Chrome height vars refresh only when the column returns to scroll rest.
 */
export function bindOrderMealsListCapSyncOnColumnScroll(): () => void {
    const column = getOrderContentColumnScrollEl();
    if (!column) {
        return () => undefined;
    }

    let frame = 0;
    const onScroll = (): void => {
        if (frame) {
            cancelAnimationFrame(frame);
        }
        frame = requestAnimationFrame(() => {
            syncOrderMealsListViewportTopCssVar();
            if (column.scrollTop < 2) {
                syncOrderStickyChromeCssVars({ force: true });
                syncOrderMealsListViewportTopCssVar({ force: true });
            }
            frame = 0;
        });
    };

    column.addEventListener("scroll", onScroll, { passive: true });
    return () => {
        column.removeEventListener("scroll", onScroll);
        if (frame) {
            cancelAnimationFrame(frame);
        }
    };
}
